import type {
  Booking,
  ConversationEntry,
  CustomerRecord,
  Deposit,
  FutureReadinessSignal,
  Piece,
} from '@/domain/types';

// Eight fully modelled hero customers. Each carries enough timeline depth to read as real
// during the play-test. Realistic casual AU vernacular. No em dashes.

interface HeroBuild {
  customer: CustomerRecord;
  bookings: Booking[];
  deposits: Deposit[];
  pieces: Piece[];
  signals: FutureReadinessSignal[];
}

// Royalty-free tattoo imagery from Unsplash. URLs hot-link with w=720&q=75 so
// they render crisp at the medium thumbnail size without ballooning bytes.
//
// 31 Jul: every URL below has been EYEBALLED, not just HEAD-checked. The previous
// set was HEAD-checked only, and 18 of its 22 images were unrelated stock that
// happened to load - a burger, a sunflower field, a Rubik's cube, a red sneaker,
// a family on a bed - all surfacing across the app as tattoo references. If you
// add one here, look at it first: a 200 response is not evidence it is a tattoo.
const U = (id: string) => `https://images.unsplash.com/photo-${id}?w=720&q=75`;
export const referencePool: Record<string, string[]> = {
  sleeve: [
    U('1604374376934-2df6fad6519b'),
    U('1679585633300-41f96e715f0e'),
    U('1612991977455-7bf9e67d899c'),
    U('1665085326630-b01fea9a613d'),
  ],
  forearm: [
    U('1575492899586-009d962fc732'),
    U('1610942933193-8fafd0973f6d'),
    U('1542727365-19732a80dcfd'),
    U('1604374376934-2df6fad6519b'),
  ],
  fineLine: [
    U('1542727365-19732a80dcfd'),
    U('1570106786245-ce68ce6503f8'),
    U('1726129088651-266bab5bf45c'),
    U('1695226304152-931956f4a474'),
  ],
  colourRealism: [
    U('1479767574301-a01c78234a0c'),
    U('1641402027551-6a2fbf05b356'),
    U('1610942933193-8fafd0973f6d'),
    U('1616879564267-a336232e3a95'),
  ],
  chicano: [
    U('1612911012211-d14e442e4739'),
    U('1532543149533-f0ed72f555c3'),
    U('1611501275019-9b5cda994e8d'),
    U('1716948942997-6454f914e38a'),
  ],
  traditional: [
    U('1479767574301-a01c78234a0c'),
    U('1641402027551-6a2fbf05b356'),
    U('1665085326630-b01fea9a613d'),
    U('1679585633300-41f96e715f0e'),
  ],
  neoTraditional: [
    U('1641402027551-6a2fbf05b356'),
    U('1665085326630-b01fea9a613d'),
    U('1479767574301-a01c78234a0c'),
    U('1695226304152-931956f4a474'),
  ],
  blackwork: [
    U('1566916501552-fc024f3ea946'),
    U('1570106786245-ce68ce6503f8'),
    U('1532543149533-f0ed72f555c3'),
    U('1612911012211-d14e442e4739'),
  ],
  backPiece: [
    U('1669437960108-ae48a1701f0c'),
    U('1695226304152-931956f4a474'),
    U('1566916501552-fc024f3ea946'),
    U('1611501275019-9b5cda994e8d'),
  ],
  lettering: [
    U('1669437960108-ae48a1701f0c'),
    U('1542727365-19732a80dcfd'),
    U('1726129088651-266bab5bf45c'),
    U('1716948943021-cea8dc2d2c3f'),
  ],
  flash: [
    U('1542744383-8c330d91f4b1'),
    U('1552627019-947c3789ffb5'),
    U('1513078094721-e7b6e0394a6a'),
    U('1568515045052-f9a854d70bfd'),
  ],
  coverUp: [
    U('1612991977455-7bf9e67d899c'),
    U('1565058379802-bbe93b2f703a'),
    U('1564426622559-5af68da63b96'),
    U('1605647533135-51b5906087d0'),
  ],
  halfSleeve: [
    U('1604374376934-2df6fad6519b'),
    U('1575492899586-009d962fc732'),
    U('1612991977455-7bf9e67d899c'),
    U('1565058379802-bbe93b2f703a'),
  ],
  chestPanel: [
    U('1612911012211-d14e442e4739'),
    U('1532543149533-f0ed72f555c3'),
    U('1597852075234-fd721ac361d3'),
    U('1716948942997-6454f914e38a'),
  ],
};

// Map of DesignType (and a few placement words) to a referencePool key so we
// can pull imagery deterministically from any procedural booking.
const DESIGN_TO_POOL: Record<string, keyof typeof referencePool> = {
  'sleeve': 'sleeve',
  'half-sleeve': 'halfSleeve',
  'back-piece': 'backPiece',
  'chest-panel': 'chestPanel',
  'black-and-grey': 'blackwork',
  'colour-realism': 'colourRealism',
  'fine-line': 'fineLine',
  'lettering': 'lettering',
  'traditional': 'traditional',
  'neo-traditional': 'neoTraditional',
  'blackwork': 'blackwork',
  'flash': 'flash',
  'cover-up': 'coverUp',
};

// Deterministic FNV-1a-ish hash so the same seed key always returns the same
// photos for the same booking across renders.
function poolHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}

export function pickReferences(
  designType: string,
  count: number,
  seedKey: string,
): string[] {
  const poolKey = DESIGN_TO_POOL[designType] ?? 'sleeve';
  const pool = referencePool[poolKey] ?? referencePool.sleeve!;
  if (pool.length === 0) return [];
  const out: string[] = [];
  const seed = poolHash(seedKey);
  for (let i = 0; i < count; i++) {
    out.push(pool[(seed + i * 2654435761) % pool.length]!);
  }
  // De-duplicate while preserving order.
  return [...new Set(out)];
}

const ISO = {
  // Sim base is Monday 2026-05-04 08:42 AEST. We mark prior events relative to it.
  fridayChat: '2026-05-01T15:30:00+10:00',
  fridayQuote: '2026-05-01T15:42:00+10:00',
  saturdayVoicemail: '2026-05-02T03:14:00+10:00',
  thursdayLastWeek: '2026-04-30T18:50:00+10:00',
  novemberLastYear: '2025-11-12T11:20:00+11:00',
  reactivationCall: '2026-05-04T13:55:00+10:00',
  aaronDmStart: '2026-05-04T09:05:00+10:00',
  jessDmStart: '2026-05-04T09:18:00+10:00',
  tylerDmStart: '2026-05-04T10:30:00+10:00',
  marcusDmStart: '2026-05-04T12:30:00+10:00',
  mayaCallStart: '2026-05-04T13:00:00+10:00',
};

const baseCustomer = (
  overrides: Pick<
    CustomerRecord,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'phone'
    | 'email'
    | 'instagramHandle'
    | 'ageBand'
    | 'suburb'
    | 'postcode'
    | 'state'
    | 'distanceToNearestStudioKm'
    | 'nearestStudioId'
    | 'primaryStudioId'
    | 'source'
    | 'leadScore'
    | 'leadStage'
    | 'toneTag'
    | 'engagementStatus'
    | 'conversation'
    | 'lifetimeSpendAud'
  > &
    Partial<CustomerRecord>,
): CustomerRecord => ({
  preferredName: undefined,
  rapportNotes: [],
  futureReadinessSignals: [],
  bookings: [],
  pieces: [],
  deposits: [],
  creditOnFile: { balanceAud: 0, history: [] },
  crossStudioTouches: [],
  doNotContact: false,
  hero: true,
  ...overrides,
});

function sarah(): HeroBuild {
  const conversation: ConversationEntry[] = [
    {
      id: 'msg-sarah-1',
      customerId: 'sarah-holman',
      channel: 'ig-dm',
      direction: 'in',
      body: 'Hey, I saw Pat\'s work and I\'m keen on a black-and-grey forearm. Half sleeve-ish. Maybe ~6 hours total. Any chance you can quote me?',
      timestamp: ISO.fridayChat,
      authorRole: 'customer',
      attachments: [
        { kind: 'image', url: referencePool.forearm![0]! },
        { kind: 'image', url: referencePool.forearm![1]! },
      ],
    },
    {
      id: 'msg-sarah-2',
      customerId: 'sarah-holman',
      channel: 'ig-dm',
      direction: 'out',
      body: 'Hey Sarah, glad you reached out. Pat does great forearm pieces. For 6 hours we\'d run a half-day, $1,200 all in, $400 deposit holds it. Want me to send the link?',
      timestamp: '2026-05-01T15:36:00+10:00',
      authorRole: 'agent',
      authorName: 'Lydia',
    },
    {
      id: 'msg-sarah-3',
      customerId: 'sarah-holman',
      channel: 'ig-dm',
      direction: 'in',
      body: 'Sweet. I\'ll think on it tonight and let you know tomorrow morning.',
      timestamp: ISO.fridayQuote,
      authorRole: 'customer',
    },
    {
      id: 'msg-sarah-4',
      customerId: 'sarah-holman',
      channel: 'voice',
      direction: 'in',
      body: 'Voicemail: "Hi, it\'s Sarah. Just calling about the deposit for Saturday. Tried to ring but missed you. Call me back any time after 8."',
      timestamp: ISO.saturdayVoicemail,
      authorRole: 'customer',
      attachments: [
        {
          kind: 'audio',
          url: '/sounds/voicemail-sarah.wav',
          durationSec: 22,
          transcript: 'Hi, it\'s Sarah. Just calling about the deposit for Saturday.',
        },
      ],
    },
  ];
  return {
    customer: baseCustomer({
      id: 'sarah-holman',
      firstName: 'Sarah',
      lastName: 'Holman',
      phone: '+61 412 553 091',
      email: 'sarah.holman@example.com',
      instagramHandle: '@sarah.holman',
      ageBand: '25-34',
      suburb: 'Brunswick',
      postcode: '3056',
      state: 'VIC',
      distanceToNearestStudioKm: 2.4,
      nearestStudioId: 'coburg',
      primaryStudioId: 'coburg',
      source: {
        channel: 'ig-dm',
        campaignId: 'meta-may-fineline',
        firstTouchAt: ISO.fridayChat,
        acquisitionCostAud: 17.4,
      },
      leadScore: 88,
      leadStage: 'deposit-pending',
      toneTag: 'casual-young',
      engagementStatus: 'active',
      conversation,
      designIntent: {
        type: 'black-and-grey',
        placement: 'right forearm',
        estimatedHours: 6,
        size: 'half-day',
        notes: 'Cherry blossom + script. Detail-heavy. Pat-style fineline.',
        referencePhotos: referencePool.forearm!,
      },
      lifetimeSpendAud: 0,
    }),
    bookings: [],
    deposits: [],
    pieces: [
      {
        id: 'piece-sarah-forearm',
        customerId: 'sarah-holman',
        title: 'Fine-line script forearm',
        designType: 'black-and-grey',
        placement: 'right forearm',
        style: 'fine-line + script',
        referencePhotos: referencePool.forearm!,
        assignedArtistId: 'pat',
        estimatedTotalHours: 6,
        hoursCompleted: 0,
        // Quoted as a half-day plus a finishing sitting. Set even though nothing
        // is booked yet, so the brief reads "session 1 of 2" the moment it is.
        estimatedTotalSessions: 2,
        sessionsCompleted: 0,
        status: 'planning',
        bookingsLinked: [],
      },
    ],
    signals: [],
  };
}

function aaronPatel(): HeroBuild {
  const conversation: ConversationEntry[] = [
    {
      id: 'msg-aaron-1',
      customerId: 'aaron-patel',
      channel: 'meta-dm',
      direction: 'in',
      body: 'Hi, I want to get a half-sleeve, black and grey, can I get a quote?',
      timestamp: ISO.aaronDmStart,
      authorRole: 'customer',
    },
    {
      id: 'msg-aaron-2',
      customerId: 'aaron-patel',
      channel: 'meta-dm',
      direction: 'out',
      body: 'Hey, awesome, half-sleeve in black-and-grey is one of our favourites. What part of the arm are you thinking? Inner, outer, forearm, full sleeve?',
      timestamp: '2026-05-04T09:06:00+10:00',
      authorRole: 'agent',
      authorName: 'AI Lead Intake',
    },
    {
      id: 'msg-aaron-3',
      customerId: 'aaron-patel',
      channel: 'meta-dm',
      direction: 'in',
      body: 'Outer forearm, maybe like 6-8 hours of work, I have some reference.',
      timestamp: '2026-05-04T09:08:00+10:00',
      authorRole: 'customer',
      attachments: [
        { kind: 'image', url: referencePool.sleeve![0]! },
        { kind: 'image', url: referencePool.sleeve![1]! },
      ],
    },
    {
      id: 'msg-aaron-4',
      customerId: 'aaron-patel',
      channel: 'meta-dm',
      direction: 'out',
      body: 'Send the refs through. Sounds like a half-day, $400 deposit holds it. Who\'s your favourite of our crew?',
      timestamp: '2026-05-04T09:09:00+10:00',
      authorRole: 'agent',
    },
  ];
  return {
    customer: baseCustomer({
      id: 'aaron-patel',
      firstName: 'Aaron',
      lastName: 'Patel',
      phone: '+61 408 221 119',
      email: 'aaron.patel@example.com',
      instagramHandle: '@aaron.p',
      ageBand: '25-34',
      suburb: 'Northcote',
      postcode: '3070',
      state: 'VIC',
      distanceToNearestStudioKm: 3.1,
      nearestStudioId: 'coburg',
      primaryStudioId: 'coburg',
      source: {
        channel: 'meta-dm',
        campaignId: 'meta-may-halfsleeve',
        firstTouchAt: ISO.aaronDmStart,
      },
      leadScore: 72,
      leadStage: 'qualifying',
      toneTag: 'casual-young',
      engagementStatus: 'active',
      conversation,
      designIntent: {
        type: 'black-and-grey',
        placement: 'outer forearm',
        estimatedHours: 7,
        size: 'half-day',
        notes: 'Half-sleeve, black-and-grey, references pending.',
        referencePhotos: referencePool.sleeve!,
      },
      lifetimeSpendAud: 0,
    }),
    bookings: [],
    deposits: [],
    pieces: [],
    signals: [],
  };
}

function jess(): HeroBuild {
  const conversation: ConversationEntry[] = [
    {
      id: 'msg-jess-1',
      customerId: 'jess-marino',
      channel: 'meta-dm',
      direction: 'in',
      body: 'Hey, looking at a full sleeve. Realism + colour. Mostly portraits and floral. Budget around $9-10k, can take it in 3-4 sessions over a few months.',
      timestamp: ISO.jessDmStart,
      authorRole: 'customer',
      attachments: [
        { kind: 'image', url: referencePool.colourRealism![0]! },
        { kind: 'image', url: referencePool.colourRealism![1]! },
      ],
    },
    {
      id: 'msg-jess-2',
      customerId: 'jess-marino',
      channel: 'meta-dm',
      direction: 'out',
      body: 'Jess, this is awesome scope. Pat\'s our colour realism lead, and Brandon also smashes this style. Want me to set up a consult on Zoom this week to scope properly?',
      timestamp: '2026-05-04T09:22:00+10:00',
      authorRole: 'agent',
    },
  ];
  return {
    customer: baseCustomer({
      id: 'jess-marino',
      firstName: 'Jess',
      lastName: 'Marino',
      phone: '+61 411 670 882',
      email: 'jess@jessmarino.com',
      instagramHandle: '@jess.marino',
      ageBand: '35-44',
      suburb: 'Carlton',
      postcode: '3053',
      state: 'VIC',
      distanceToNearestStudioKm: 1.8,
      nearestStudioId: 'coburg',
      primaryStudioId: 'coburg',
      source: {
        channel: 'meta-dm',
        campaignId: 'meta-fullsleeve-spring',
        firstTouchAt: ISO.jessDmStart,
      },
      leadScore: 95,
      leadStage: 'qualifying',
      toneTag: 'casual-older',
      engagementStatus: 'active',
      conversation,
      designIntent: {
        type: 'colour-realism',
        placement: 'full sleeve',
        estimatedHours: 32,
        size: 'multi-session',
        notes: 'Portraits + floral, $9-10k, 3-4 sessions.',
        referencePhotos: referencePool.colourRealism!,
      },
      // Equals the sum of this customer's completed sittings, counted the way the
      // artist's client-history rail counts them. They sit on the clean 4-May board,
      // so the rail is read beside this figure and any disagreement shows on screen.
      // Guarded by fixtures.test.ts.
      lifetimeSpendAud: 4800,
    }),
    bookings: [],
    deposits: [],
    pieces: [],
    signals: [],
  };
}

function tyler(): HeroBuild {
  const conversation: ConversationEntry[] = [
    {
      id: 'msg-tyler-1',
      customerId: 'tyler-okafor',
      channel: 'meta-dm',
      direction: 'in',
      body: 'I want a chest piece, traditional style. What\'s your cancellation policy if I have to reschedule?',
      timestamp: ISO.tylerDmStart,
      authorRole: 'customer',
      attachments: [
        { kind: 'image', url: referencePool.traditional![0]! },
      ],
    },
    {
      id: 'msg-tyler-2',
      customerId: 'tyler-okafor',
      channel: 'meta-dm',
      direction: 'out',
      body: 'Great question, I\'ll get Georgina to call you in the next five minutes to walk you through it properly.',
      timestamp: '2026-05-04T10:32:00+10:00',
      authorRole: 'agent',
    },
  ];
  return {
    customer: baseCustomer({
      id: 'tyler-okafor',
      firstName: 'Tyler',
      lastName: 'Okafor',
      phone: '+61 433 902 765',
      email: 'tyler.okafor@example.com',
      instagramHandle: '@tyler.ok',
      ageBand: '25-34',
      suburb: 'Footscray',
      postcode: '3011',
      state: 'VIC',
      distanceToNearestStudioKm: 5.6,
      nearestStudioId: 'coburg',
      primaryStudioId: 'coburg',
      source: {
        channel: 'meta-dm',
        campaignId: 'meta-traditional-chest',
        firstTouchAt: ISO.tylerDmStart,
      },
      leadScore: 60,
      leadStage: 'qualifying',
      toneTag: 'professional',
      engagementStatus: 'active',
      conversation,
      designIntent: {
        type: 'traditional',
        placement: 'chest',
        estimatedHours: 8,
        size: 'full-day',
        notes: 'Cancellation-conscious. Tone: cautious.',
        referencePhotos: referencePool.traditional!,
      },
      // Equals the sum of this customer's completed sittings, counted the way the
      // artist's client-history rail counts them. They sit on the clean 4-May board,
      // so the rail is read beside this figure and any disagreement shows on screen.
      // Guarded by fixtures.test.ts.
      lifetimeSpendAud: 5800,
    }),
    bookings: [],
    deposits: [],
    pieces: [],
    signals: [],
  };
}

function daniel(): HeroBuild {
  const conversation: ConversationEntry[] = [
    {
      id: 'msg-daniel-1',
      customerId: 'daniel-pham',
      channel: 'voice',
      direction: 'in',
      body: 'Phone call (5m 12s). Talked through half-sleeve, mid-May bonus, no rush.',
      timestamp: ISO.thursdayLastWeek,
      authorRole: 'agent',
      authorName: 'Lydia',
      futureReadinessSignal: {
        triggerDate: '2026-05-15T09:30:00+10:00',
        rawPhrase: 'after my bonus, mid-May',
      },
    },
  ];
  return {
    customer: baseCustomer({
      id: 'daniel-pham',
      firstName: 'Daniel',
      lastName: 'Pham',
      phone: '+61 422 110 533',
      email: 'daniel.pham@example.com',
      instagramHandle: '@dphamink',
      ageBand: '25-34',
      suburb: 'Reservoir',
      postcode: '3073',
      state: 'VIC',
      distanceToNearestStudioKm: 4.2,
      nearestStudioId: 'coburg',
      primaryStudioId: 'coburg',
      source: {
        channel: 'voice',
        firstTouchAt: ISO.thursdayLastWeek,
      },
      leadScore: 70,
      leadStage: 'qualifying',
      toneTag: 'casual-young',
      engagementStatus: 'warm',
      conversation,
      futureReadinessSignals: [
        {
          id: 'sig-daniel-may',
          customerId: 'daniel-pham',
          rawPhrase: 'after my bonus, mid-May',
          scheduledFor: '2026-05-15T09:30:00+10:00',
          recommendedChannel: 'sms',
          status: 'scheduled',
          createdAt: ISO.thursdayLastWeek,
          draftMessage:
            'Hey Daniel, you mentioned May would be good for the half-sleeve. Want to lock in a date? I have Pat\'s availability open.',
        },
      ],
      designIntent: {
        type: 'black-and-grey',
        placement: 'right half-sleeve',
        estimatedHours: 6,
        size: 'half-day',
        notes: 'Saving for bonus. May lock-in.',
        referencePhotos: referencePool.fineLine!,
      },
      lifetimeSpendAud: 0,
    }),
    bookings: [],
    deposits: [],
    pieces: [],
    signals: [
      {
        id: 'sig-daniel-may',
        customerId: 'daniel-pham',
        rawPhrase: 'after my bonus, mid-May',
        scheduledFor: '2026-05-15T09:30:00+10:00',
        recommendedChannel: 'sms',
        status: 'scheduled',
        createdAt: ISO.thursdayLastWeek,
        draftMessage:
          'Hey Daniel, you mentioned May would be good for the half-sleeve. Want to lock in a date? I have Pat\'s availability open.',
      },
    ],
  };
}

function marcus(): HeroBuild {
  const conversation: ConversationEntry[] = [
    {
      id: 'msg-marcus-1',
      customerId: 'marcus-vella',
      channel: 'ig-dm',
      direction: 'in',
      body: 'Hey, I want a colour realism sleeve, can I book with you?',
      timestamp: ISO.marcusDmStart,
      authorRole: 'customer',
      attachments: [
        { kind: 'image', url: referencePool.colourRealism![0]! },
        { kind: 'image', url: referencePool.colourRealism![2]! },
      ],
    },
  ];
  return {
    customer: baseCustomer({
      id: 'marcus-vella',
      firstName: 'Marcus',
      lastName: 'Vella',
      phone: '+61 415 887 002',
      email: 'marcus.vella@example.com',
      instagramHandle: '@marcus.vella',
      ageBand: '25-34',
      suburb: 'Gosford',
      postcode: '2250',
      state: 'NSW',
      distanceToNearestStudioKm: 1.5,
      nearestStudioId: 'central-coast',
      primaryStudioId: 'central-coast',
      source: {
        channel: 'ig-dm',
        firstTouchAt: ISO.marcusDmStart,
      },
      leadScore: 80,
      leadStage: 'qualifying',
      toneTag: 'casual-young',
      engagementStatus: 'active',
      conversation,
      designIntent: {
        type: 'colour-realism',
        placement: 'outer arm',
        estimatedHours: 18,
        size: 'multi-session',
        notes: 'Direct-to-artist, lives 880km from Brandon\'s studio.',
        referencePhotos: referencePool.colourRealism!,
      },
      crossStudioTouches: ['coburg'],
      lifetimeSpendAud: 0,
    }),
    bookings: [],
    deposits: [],
    pieces: [],
    signals: [],
  };
}

function aaronSingh(): HeroBuild {
  const conversation: ConversationEntry[] = [
    {
      id: 'msg-aaronsingh-1',
      customerId: 'aaron-singh',
      channel: 'voice',
      direction: 'in',
      body: 'Original consult call. Locked in Chicano half-sleeve, $400 deposit paid.',
      timestamp: '2024-10-04T11:00:00+11:00',
      authorRole: 'agent',
      authorName: 'Lydia',
    },
    {
      id: 'msg-aaronsingh-2',
      customerId: 'aaron-singh',
      channel: 'sms',
      direction: 'in',
      body: 'Hey life got busy, sorry. Will rebook soon promise.',
      timestamp: '2025-02-12T18:11:00+11:00',
      authorRole: 'customer',
    },
    {
      id: 'msg-aaronsingh-3',
      customerId: 'aaron-singh',
      channel: 'voice',
      direction: 'out',
      body: 'Retell AI reactivation call. Sara from Coburg. Aaron said yes, want me to lock a date with Pat.',
      timestamp: ISO.reactivationCall,
      authorRole: 'agent',
      authorName: 'AI voice (Sara)',
      attachments: [{ kind: 'audio', url: '/sounds/reactivation-aaron.wav', durationSec: 64 }],
    },
  ];
  return {
    customer: baseCustomer({
      id: 'aaron-singh',
      firstName: 'Aaron',
      lastName: 'Singh',
      phone: '+61 414 552 988',
      email: 'aaron.singh@example.com',
      instagramHandle: '@aaron.singh',
      medicalProfile: {
        allergies: ['latex'],
        notes: 'Prefers numbing cream on longer sittings.',
        lastUpdatedAt: '2025-11-18T10:20:00+11:00',
        lastUpdatedBy: 'Georgina',
      },
      ageBand: '35-44',
      suburb: 'Pascoe Vale',
      postcode: '3044',
      state: 'VIC',
      distanceToNearestStudioKm: 3.8,
      nearestStudioId: 'coburg',
      primaryStudioId: 'coburg',
      source: {
        channel: 'voice',
        firstTouchAt: '2024-10-04T11:00:00+11:00',
      },
      leadScore: 84,
      leadStage: 'lapsed',
      toneTag: 'warm-familiar',
      engagementStatus: 'warm',
      conversation,
      designIntent: {
        type: 'half-sleeve',
        placement: 'left half-sleeve',
        estimatedHours: 8,
        size: 'full-day',
        notes: 'Chicano. Half-finished. Pat to wrap.',
        referencePhotos: referencePool.chicano!,
      },
      creditOnFile: {
        balanceAud: 200,
        expiryDate: '2026-10-04T00:00:00+10:00',
        history: [
          {
            id: 'cof-aaron-1',
            type: 'added',
            amountAud: 200,
            note: 'Roll-over after rescheduled 2025 session.',
            timestamp: '2025-02-15T10:00:00+11:00',
          },
        ],
      },
      // Every completed sitting Aaron has, across all the seeds that touch him:
      // 4 x $950 half-days + 2 x $320 script sittings here and in the floor seed
      // ($4,440), the eight procedural leaderboard sittings that also name him
      // ($6,200), and his 10am sitting on the clean 4-May board ($1,000). Keep
      // this equal to that total - fixtures.test.ts asserts it, because the
      // artist's client-history rail sums those sittings right next to this
      // figure, so any disagreement reads as a bug on screen.
      lifetimeSpendAud: 11640,
    }),
    bookings: [
      // The half-sleeve sitting that already existed in all but name: the piece
      // carried this date as `lastSessionDate` and a "Session 1 linework wrap"
      // photo, but no booking recorded it. Aaron then went quiet (see the Feb
      // 2025 message above), which is why the piece has sat since.
      {
        id: 'bk-aaron-halfsleeve-s1',
        customerId: 'aaron-singh',
        studioId: 'coburg',
        artistId: 'pat',
        pieceId: 'piece-aaron-singh-chicano',
        start: '2024-11-09T11:00:00+11:00',
        durationMin: 240,
        sessionLength: 'half-day',
        status: 'completed',
        designBrief: 'Chicano half-sleeve · session 1 · linework',
        bodyPart: 'upper-arm',
        bodyPartSide: 'left',
        referencePhotos: referencePool.chicano!.slice(0, 2),
        source: 'phone-inbound',
        createdAt: '2024-10-04T11:30:00+11:00',
        checkedInAt: '2024-11-09T11:00:00+11:00',
        checkedOutAt: '2024-11-09T15:00:00+11:00',
        invoiceStatus: 'paid',
        amountPaidAud: 950,
        paymentMethod: 'card',
        paidAt: '2024-11-09T15:05:00+11:00',
        sessionNotes:
          'Linework done, sat well. Wants the shading booked before winter — chase if he goes quiet.',
      },
      // A small piece finished alongside the sleeve. Two short sittings, closed
      // out — the one piece on Aaron's record that shows a start-to-end span.
      aaronScriptSession(1, '2026-02-12'),
      aaronScriptSession(2, '2026-03-19'),
    ],
    deposits: [],
    pieces: [
      {
        id: 'piece-aaron-singh-chicano',
        customerId: 'aaron-singh',
        title: 'Chicano half-sleeve',
        designType: 'half-sleeve',
        placement: 'left half-sleeve',
        style: 'chicano',
        referencePhotos: referencePool.chicano!,
        progressPhotos: [
          {
            id: 'pp-aaron-singh-chicano-1',
            url: referencePool.chicano![0]!,
            capturedAt: '2024-11-09T15:00:00+11:00',
            bookingId: 'bk-aaron-halfsleeve-s1',
            caption: 'Session 1 linework wrap.',
          },
        ],
        assignedArtistId: 'pat',
        estimatedTotalHours: 12,
        hoursCompleted: 4,
        estimatedTotalSessions: 3,
        sessionsCompleted: 1,
        status: 'in-progress',
        bookingsLinked: ['bk-aaron-halfsleeve-s1'],
        lastSessionDate: '2024-11-09T11:00:00+11:00',
      },
      {
        id: 'piece-aaron-singh-script',
        customerId: 'aaron-singh',
        title: 'Script lettering',
        designType: 'lettering',
        placement: 'left inner forearm',
        style: 'fine-line script',
        referencePhotos: referencePool.lettering!,
        assignedArtistId: 'pat',
        estimatedTotalHours: 4,
        hoursCompleted: 4,
        estimatedTotalSessions: 2,
        sessionsCompleted: 2,
        status: 'completed',
        bookingsLinked: ['bk-aaron-script-s1', 'bk-aaron-script-s2'],
        lastSessionDate: '2026-03-19T10:00:00+10:00',
      },
    ],
    signals: [],
  };
}

/**
 * One sitting on Aaron's finished script piece. Two hours at Pat's `star` small
 * rate ($320) - so the two sittings account for exactly the 4h the piece claims.
 */
function aaronScriptSession(n: 1 | 2, dayIso: string): Booking {
  return {
    id: `bk-aaron-script-s${n}`,
    customerId: 'aaron-singh',
    studioId: 'coburg',
    artistId: 'pat',
    pieceId: 'piece-aaron-singh-script',
    start: `${dayIso}T10:00:00+10:00`,
    durationMin: 120,
    sessionLength: 'small',
    status: 'completed',
    designBrief: `Script lettering · session ${n} of 2`,
    bodyPart: 'forearm',
    bodyPartSide: 'left',
    bodyPartQualifier: 'inner',
    referencePhotos: [],
    source: 'in-chair-rebooking',
    createdAt: `${dayIso}T09:00:00+10:00`,
    checkedInAt: `${dayIso}T10:00:00+10:00`,
    checkedOutAt: `${dayIso}T12:00:00+10:00`,
    invoiceStatus: 'paid',
    amountPaidAud: 320,
    paymentMethod: 'card',
    paidAt: `${dayIso}T12:05:00+10:00`,
  };
}

function maya(): HeroBuild {
  const conversation: ConversationEntry[] = [
    {
      id: 'msg-maya-1',
      customerId: 'maya-chen',
      channel: 'sms',
      direction: 'in',
      body: 'Hi, I had a fine-line piece at Coburg about 8 months ago and I want to add to it. I\'m in Frankston now, is there an artist there who does similar?',
      timestamp: '2026-04-21T14:00:00+10:00',
      authorRole: 'customer',
      attachments: [{ kind: 'image', url: referencePool.fineLine![0]! }],
    },
    {
      id: 'msg-maya-2',
      customerId: 'maya-chen',
      channel: 'sms',
      direction: 'out',
      body: 'Hi Maya, yes, Indie at Frankston specialises in fine-line. Happy to set up a consult. We have your record already.',
      timestamp: '2026-04-21T14:32:00+10:00',
      authorRole: 'agent',
      authorName: 'Jordan',
    },
    {
      id: 'msg-maya-3',
      customerId: 'maya-chen',
      channel: 'voice',
      direction: 'in',
      body: 'Inbound call to Frankston. Asking about availability next month.',
      timestamp: ISO.mayaCallStart,
      authorRole: 'customer',
    },
  ];
  return {
    customer: baseCustomer({
      id: 'maya-chen',
      firstName: 'Maya',
      lastName: 'Chen',
      phone: '+61 466 781 010',
      email: 'maya.chen@example.com',
      instagramHandle: '@mayasart',
      ageBand: '25-34',
      suburb: 'Frankston',
      postcode: '3199',
      state: 'VIC',
      distanceToNearestStudioKm: 0.6,
      nearestStudioId: 'frankston',
      primaryStudioId: 'frankston',
      source: {
        channel: 'voice',
        firstTouchAt: '2025-09-12T15:00:00+10:00',
      },
      leadScore: 76,
      leadStage: 'qualifying',
      toneTag: 'warm-familiar',
      engagementStatus: 'warm',
      conversation,
      designIntent: {
        type: 'fine-line',
        placement: 'right ribs',
        estimatedHours: 3,
        size: 'half-day',
        notes: 'Add to fine-line piece from Coburg 8 months ago.',
        referencePhotos: referencePool.fineLine!,
      },
      crossStudioTouches: ['coburg'],
      lifetimeSpendAud: 380,
      futureReadinessSignals: [],
    }),
    bookings: [],
    deposits: [],
    pieces: [],
    signals: [],
  };
}

export function buildHeroes(): HeroBuild[] {
  return [sarah(), aaronPatel(), jess(), tyler(), daniel(), marcus(), aaronSingh(), maya()];
}
